/**
 * A client-side mirror of `apps/api`'s `domain/component.ts`.
 *
 * ## Why duplicate a rule instead of just letting the API reject it
 *
 * The same reason `feature-flags/schemas/flag-key.ts` gives, and with the same
 * stakes: the API's rejection arrives after the operator has filled in the rest
 * of the form, and it names the one field that can never be changed afterwards.
 * A component id is permanent — grants are written under it, and nothing
 * renames or reuses one — so a typo caught on submit is a dead row in the
 * catalog, while a typo caught on the third keystroke is nothing at all.
 *
 * The API still validates. This is a convenience, not a boundary.
 *
 * ## Why the charset is the grant charset
 *
 * The API's `component.ts` decides that a component id *is* the branded grant
 * id envelope, because activating a component writes `{ kind: 'component', id }`
 * into an override row's `granted_by`. An id this form accepted and the grant
 * refused would be a component that can be created and never granted. Widening
 * this pattern past the API's `isFlagGrantId` reopens exactly that gap.
 *
 * @see apps/api/src/modules/feature-flags/domain/component.ts — the authority
 */

/** Mirrors the API's `BRANDED_ID_PATTERN` charset half. No `.`, unlike a flag key. */
const COMPONENT_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

export const MAX_COMPONENT_ID_LENGTH = 64;

/** Why a candidate id was refused. Mirrors the API's `ComponentIdRejection`. */
export type ComponentIdRejection = 'empty' | 'malformed';

/**
 * Classify a candidate without throwing. Returns `null` when the id is valid.
 *
 * Trims first, as the API's `parseComponentId` does — an id pasted with a
 * trailing space is a typo, not a second component.
 */
export function rejectComponentId(candidate: string): ComponentIdRejection | null {
    const value = candidate.trim();

    if (value.length === 0) return 'empty';
    if (value.length > MAX_COMPONENT_ID_LENGTH) return 'malformed';
    if (!COMPONENT_ID_PATTERN.test(value)) return 'malformed';

    return null;
}

/**
 * The operator-facing explanation. One sentence per rejection, matching the
 * API's own wording so the two cannot say different things about one id.
 *
 * `malformed` covers both a bad character and an over-long id, as the API's
 * single message does: splitting them here would promise a precision the server
 * does not have, and an over-long id is almost always a pasted sentence.
 */
export function explainComponentIdRejection(rejection: ComponentIdRejection): string {
    switch (rejection) {
        case 'empty':
            return 'A component id is required.';
        case 'malformed':
            return `Use 1-${String(MAX_COMPONENT_ID_LENGTH)} characters of A-Z, a-z, 0-9, '_' or '-'. Unlike a flag key, an id has no dots.`;
    }
}
